import React, { useState } from 'react';
import { 
  Avatar, 
  Button, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Divider,
  Form,
  Input,
  message,
  Modal,
  Statistic,
  Space,
  Tag,
  Table,
  Select,
  Badge,
  Tooltip,
  Transfer
} from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import type { Key } from 'antd/es/table/interface';
import { 
  UserOutlined, 
  LeftOutlined,
  BankOutlined,
  ArrowRightOutlined,
  TeamOutlined,
  DollarOutlined,
  ShareAltOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 模拟佣金数据
const commissions = [
  {
    id: '1',
    amount: 320,
    status: 'paid',
  },
  {
    id: '2',
    amount: 258,
    status: 'approved',
  },
];

// 模拟二维码图片URL (实际项目中应替换为真实的二维码图片URL)
const qrCodeUrl = 'https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png';

// 模拟优惠券数据
const availableCoupons = [
  {
    id: '1',
    name: '住房8.8折优惠券',
    type: 'room',
    discount: '88%',
    validDays: 30,
  },
  {
    id: '2',
    name: '餐饮立减50元券',
    type: 'dining',
    discount: '￥50',
    validDays: 15,
  },
  {
    id: '3',
    name: 'SPA护理体验券',
    type: 'spa',
    discount: '特惠价288元',
    validDays: 60,
  },
];

// 模拟客户数据
const customerList = [
  {
    key: '1',
    name: '张先生',
    phone: '138****1234',
    tags: ['高频客户', 'VIP'],
  },
  {
    key: '2',
    name: '李女士',
    phone: '139****5678',
    tags: ['新客户'],
  },
  {
    key: '3',
    name: '王先生',
    phone: '137****9012',
    tags: ['商务客户', '常客'],
  },
];

// 模拟优惠券发送记录
const couponRecords = [
  {
    id: '1',
    customerName: '张先生',
    couponName: '住房8.8折优惠券',
    type: 'room',
    sendTime: '2024-03-15 14:30',
    status: 'unused',
    greeting: '祝您入住愉快！',
  },
  {
    id: '2',
    customerName: '李女士',
    couponName: '餐饮立减50元券',
    type: 'dining',
    sendTime: '2024-03-14 16:20',
    status: 'used',
    greeting: '欢迎品尝我们的美食！',
  },
];

const EmployeePromotion: React.FC = () => {
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const [posterModalVisible, setPosterModalVisible] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  
  const [sendCouponModalVisible, setSendCouponModalVisible] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<Key[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState<string>();
  const [greeting, setGreeting] = useState('');
  const [couponRecordsVisible, setCouponRecordsVisible] = useState(false);
  
  const handleWithdrawalSubmit = () => {
    form.validateFields()
      .then(values => {
        console.log('提现申请:', values);
        setWithdrawalModalVisible(false);
        form.resetFields();
        message.success('提现申请已提交，请等待审核');
      })
      .catch(info => {
        console.log('表单验证失败:', info);
      });
  };

  // 账户数据
  const accountBalance = 578; // 账户余额
  const pendingIncome = 120; // 待入账
  const withdrawing = 200; // 提现中
  const withdrawn = 1500; // 已提现
  
  // 统计数据
  const customerCount = 12; // 客户数量
  const totalCommission = commissions.reduce((acc, curr) => acc + curr.amount, 0); // 累计佣金
  
  // 计算可提现佣金
  const availableCommission = accountBalance;
  
  // 显示推广海报
  const showPoster = () => {
    setPosterModalVisible(true);
  };
  
  // 保存推广海报
  const savePoster = () => {
    message.success('推广海报保存成功！');
    // 实际项目中这里可以实现图片保存功能
  };
  
  // 分享推广海报
  const sharePoster = () => {
    message.success('分享功能已激活，请选择分享方式');
    // 实际项目中这里可以调用原生分享API
  };

  // 优惠券记录表格列定义
  const couponColumns = [
    {
      title: '客户姓名',
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: '优惠券类型',
      dataIndex: 'couponName',
      key: 'couponName',
    },
    {
      title: '发送时间',
      dataIndex: 'sendTime',
      key: 'sendTime',
    },
    {
      title: '使用状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Badge 
          status={status === 'used' ? 'success' : 'processing'} 
          text={status === 'used' ? '已使用' : '未使用'} 
        />
      ),
    },
    {
      title: '祝福语',
      dataIndex: 'greeting',
      key: 'greeting',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
  ];

  // 发送优惠券
  const handleSendCoupon = () => {
    if (!selectedCouponId || selectedCustomers.length === 0) {
      message.error('请选择优惠券和发送对象');
      return;
    }
    
    message.success('优惠券发送成功！');
    setSendCouponModalVisible(false);
    setSelectedCustomers([]);
    setSelectedCouponId(undefined);
    setGreeting('');
  };

  // 处理客户选择变化
  const handleCustomerChange = (nextTargetKeys: Key[], direction: TransferDirection, moveKeys: Key[]) => {
    setSelectedCustomers(nextTargetKeys);
  };

  return (
    <div style={{ 
      maxWidth: 500, 
      margin: '0 auto', 
      background: '#f5f5f5', 
      minHeight: '100vh'
    }}>
      {/* 顶部标题栏 */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#fff', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            type="text" 
            icon={<LeftOutlined />} 
            onClick={() => navigate(-1)}
            style={{ marginRight: 16 }}
          />
          <div style={{ fontSize: 16, fontWeight: 'bold' }}>我的推广</div>
        </div>
        <Button 
          type="primary" 
          shape="round" 
          icon={<ShareAltOutlined />} 
          onClick={showPoster}
          style={{ backgroundColor: '#529C8A', borderColor: '#529C8A' }}
        >
          我要推广
        </Button>
      </div>
      
      {/* 个人信息头部 */}
      <div style={{ 
        padding: '24px 16px',
        background: '#fff', 
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#529C8A' }} />
        <div style={{ marginLeft: 16 }}>
          <Title level={5} style={{ margin: 0 }}>顾静静</Title>
          <Text type="secondary">秋果酒店 - 营销专员</Text>
        </div>
      </div>
      
      {/* 主内容区 - 推广数据内容 */}
      <div style={{ padding: '16px' }}>
        {/* 账户信息区域 */}
        <Card style={{ marginBottom: 16 }}>
          <Title level={5}>账户信息</Title>
          <Row gutter={[16, 24]}>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Statistic 
                  title="账户余额(元)" 
                  value={accountBalance} 
                  precision={2}
                  valueStyle={{ color: '#529C8A' }}
                />
                <Button 
                  type="primary" 
                  size="small"
                  style={{ marginTop: 8, backgroundColor: '#529C8A', borderColor: '#529C8A' }}
                  onClick={() => setWithdrawalModalVisible(true)}
                >
                  提现
                </Button>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Statistic 
                  title="待入账(元)" 
                  value={pendingIncome} 
                  precision={2}
                  valueStyle={{ color: '#faad14' }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Statistic 
                  title="提现中(元)" 
                  value={withdrawing} 
                  precision={2}
                  valueStyle={{ color: '#1890ff' }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center' }}>
                <Statistic 
                  title="已提现(元)" 
                  value={withdrawn} 
                  precision={2}
                  valueStyle={{ color: '#52c41a' }}
                />
              </div>
            </Col>
          </Row>
        </Card>
        
        {/* 重要数据统计 */}
        <Card>
          <Title level={5}>重要数据统计</Title>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card>
                <div style={{ cursor: 'pointer' }} onClick={() => navigate('/mobile-preview/customers')}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <TeamOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 8 }} />
                      <Text>我的客户</Text>
                    </div>
                    <ArrowRightOutlined />
                  </div>
                  <Title level={3} style={{ margin: '16px 0 0 0', color: '#1890ff' }}>{customerCount}</Title>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <div style={{ cursor: 'pointer' }} onClick={() => navigate('/mobile-preview/commissions')}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <DollarOutlined style={{ fontSize: 24, color: '#52c41a', marginRight: 8 }} />
                      <Text>累计佣金(元)</Text>
                    </div>
                    <ArrowRightOutlined />
                  </div>
                  <Title level={3} style={{ margin: '16px 0 0 0', color: '#52c41a' }}>{totalCommission.toFixed(2)}</Title>
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
        
        {/* 推广说明 */}
        <Card style={{ marginTop: 16 }}>
          <Title level={5}>推广说明</Title>
          <Divider style={{ margin: '8px 0 16px' }} />
          <div style={{ fontSize: 14, color: '#666' }}>
            <p>1. 通过您的专属推广链接或二维码邀请客户</p>
            <p>2. 客户下单后，系统自动记录佣金</p>
            <p>3. 佣金在客户确认后自动进入您的账户</p>
            <p>4. 账户余额可随时申请提现至银行卡</p>
          </div>
        </Card>

        {/* 优惠券发送入口 */}
        <Card style={{ marginTop: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card 
                hoverable 
                onClick={() => navigate('/mobile-preview/send-promotion')}
              >
                <div style={{ textAlign: 'center' }}>
                  <GiftOutlined style={{ fontSize: 24, color: '#529C8A', marginBottom: 8 }} />
                  <div>发送优惠券</div>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card 
                hoverable 
                onClick={() => setCouponRecordsVisible(true)}
              >
                <div style={{ textAlign: 'center' }}>
                  <SendOutlined style={{ fontSize: 24, color: '#529C8A', marginBottom: 8 }} />
                  <div>发送记录</div>
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
      </div>

      {/* 提现弹窗 */}
      <Modal 
        title="申请提现" 
        open={withdrawalModalVisible}
        onCancel={() => setWithdrawalModalVisible(false)}
        onOk={handleWithdrawalSubmit}
        okText="提交"
        cancelText="取消"
        okButtonProps={{ style: { backgroundColor: '#529C8A', borderColor: '#529C8A' } }}
      >
        <Form 
          form={form}
          layout="vertical"
        >
          <Form.Item>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Text>可提现金额: </Text>
              <Text strong style={{ fontSize: 20, color: '#529C8A' }}>¥{availableCommission.toFixed(2)}</Text>
            </div>
          </Form.Item>
          <Form.Item
            name="amount"
            label="提现金额"
            rules={[
              { required: true, message: '请输入提现金额' },
              { 
                validator: (_, value) => {
                  if (value && Number(value) > availableCommission) {
                    return Promise.reject('提现金额不能大于可提现金额');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input prefix="￥" suffix="元" placeholder="请输入提现金额" />
          </Form.Item>
          <Form.Item
            name="bank"
            label="银行账户"
            rules={[{ required: true, message: '请输入银行账户' }]}
          >
            <Input placeholder="请输入银行账户" prefix={<BankOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 推广海报弹窗 */}
      <Modal
        title={null}
        open={posterModalVisible}
        onCancel={() => setPosterModalVisible(false)}
        footer={[
          <Button key="save" onClick={savePoster} icon={<QrcodeOutlined />}>
            保存海报
          </Button>,
          <Button key="share" type="primary" onClick={sharePoster} icon={<ShareAltOutlined />} style={{ backgroundColor: '#529C8A', borderColor: '#529C8A' }}>
            分享
          </Button>,
        ]}
        centered
        width={380}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ 
          padding: '24px', 
          background: 'linear-gradient(180deg, #f6fffd 0%, #ffffff 100%)',
          borderRadius: '8px 8px 0 0',
          textAlign: 'center',
        }}>
          {/* 海报头部 */}
          <div style={{ marginBottom: 20 }}>
            <Title level={4} style={{ marginBottom: 8, color: '#529C8A' }}>秋果官方认证专属管家</Title>
            <Text style={{ fontSize: 14, color: '#666' }}>专业解答 × 独家优惠 × 极速响应</Text>
          </div>
          
          {/* 个人信息 */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
            <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: '#529C8A', marginBottom: 12 }} />
            <Title level={5} style={{ marginBottom: 4 }}>顾静静</Title>
            <Tag color="#529C8A" style={{ borderRadius: 12, padding: '0 16px', fontSize: 14, marginTop: 6 }}>秋果认证专属管家</Tag>
          </div>
          
          {/* 二维码 */}
          <div style={{ marginBottom: 20 }}>
            <img 
              src={qrCodeUrl} 
              alt="专属二维码" 
              style={{ 
                width: 220, 
                height: 220, 
                display: 'block', 
                margin: '0 auto 16px',
                border: '4px solid #ffffff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }} 
            />
            <Text style={{ fontSize: 14, color: '#666' }}>扫一扫加专属管家，享一站式VIP服务！</Text>
          </div>
          
          {/* 服务特点 */}
          <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginTop: 10 }}>
            <Title level={5} style={{ textAlign: 'center', marginBottom: 16 }}>优质服务</Title>
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutlined style={{ color: '#529C8A', marginRight: 8, fontSize: 18 }} />
                <Text style={{ fontSize: 16 }}>1对1专属解答、极速响应</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutlined style={{ color: '#529C8A', marginRight: 8, fontSize: 18 }} />
                <Text style={{ fontSize: 16 }}>专享定制方案/价格优惠</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleOutlined style={{ color: '#529C8A', marginRight: 8, fontSize: 18 }} />
                <Text style={{ fontSize: 16 }}>全流程跟进/售后无忧</Text>
              </div>
            </Space>
          </Card>
        </div>
      </Modal>

      {/* 发送优惠券弹窗 */}
      <Modal
        title="发送优惠券"
        open={sendCouponModalVisible}
        onCancel={() => setSendCouponModalVisible(false)}
        onOk={handleSendCoupon}
        width={700}
        okText="发送"
        cancelText="取消"
      >
        <Form layout="vertical">
          <Form.Item 
            label="选择优惠券" 
            required
          >
            <Select
              placeholder="请选择要发送的优惠券"
              value={selectedCouponId}
              onChange={setSelectedCouponId}
              style={{ width: '100%' }}
            >
              {availableCoupons.map(coupon => (
                <Select.Option key={coupon.id} value={coupon.id}>
                  <Space>
                    <span>{coupon.name}</span>
                    <Tag color="blue">{coupon.discount}</Tag>
                    <Tag color="green">有效期{coupon.validDays}天</Tag>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            label="选择发送对象" 
            required
          >
            <Transfer
              dataSource={customerList}
              titles={['可选客户', '已选客户']}
              targetKeys={selectedCustomers}
              onChange={handleCustomerChange}
              render={item => (
                <Space>
                  <span>{item.name}</span>
                  <span style={{ color: '#999' }}>{item.phone}</span>
                  {item.tags.map((tag: string) => (
                    <Tag key={tag} color="blue">{tag}</Tag>
                  ))}
                </Space>
              )}
              listStyle={{
                width: 300,
                height: 300,
              }}
            />
          </Form.Item>

          <Form.Item 
            label="个性化祝语"
          >
            <TextArea
              rows={4}
              value={greeting}
              onChange={e => setGreeting(e.target.value)}
              placeholder="请输入要发送给客户的祝福语（选填）"
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 优惠券发送记录弹窗 */}
      <Modal
        title="优惠券发送记录"
        open={couponRecordsVisible}
        onCancel={() => setCouponRecordsVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={couponColumns}
          dataSource={couponRecords}
          rowKey="id"
          pagination={{
            pageSize: 5,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Modal>
    </div>
  );
};

export default EmployeePromotion; 